import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Tag, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import ServicesContext from '@totem/components/context/services/servicesContext';
import CommissioningReportContext from '@totem/components/salesOrders/commissioning/CommissioningReportContext';
import { SalesOrderCommissioningReportItem } from '@totem/components/salesOrders/commissioning/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { emptyId } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { DateStringToUTCDate } from '@totem/utilities/dateUtilities';

const ISSUE_FILTERS = [
  { text: 'No Building', value: 'buildingIsMissing' },
  { text: 'No Device', value: 'deviceIsMissing' },
  { text: 'Missing Tool Key', value: 'deviceToolKeyMissing' },
];

const CommissioningReportTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { loading, reportData, searchText, onAction } = useContext(
    CommissioningReportContext,
  );
  const { services } = useContext(ServicesContext);

  const toolKeysByDisplayName = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const svc of services) {
      if (svc.commissioning?.toolKeys?.length > 0) {
        map.set(svc.displayName, svc.commissioning.toolKeys);
      }
    }
    return map;
  }, [services]);

  const canEdit = permissions.contractsEditor.hasPermissions(userProfile);
  const canCreateTicket =
    permissions.ticketingCreator.hasPermissions(userProfile);
  const [pageSize, setPageSize] = useState<number>(10);

  const filteredData = useMemo(() => {
    if (!searchText) {
      return reportData ?? [];
    }
    const lower = searchText.toLowerCase();
    return (reportData ?? []).filter((item) =>
      (item.salesOrder.nsSalesOrder?.tranId ?? '')
        .toLowerCase()
        .includes(lower),
    );
  }, [reportData, searchText]);

  const organizationFilters = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of reportData ?? []) {
      const org = item.salesOrder.organization;
      if (org?.id) {
        seen.set(org.id, org.name);
      }
    }
    return Array.from(seen.entries())
      .map(([val, txt]) => ({ text: txt, value: val }))
      .sort((chkA, chkB) => chkA.text.localeCompare(chkB.text));
  }, [reportData]);

  const buildingFilters = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of reportData ?? []) {
      const bld = item.salesOrder.building;
      if (bld?.id && bld.id !== emptyId) {
        seen.set(bld.id, bld.name);
      } else {
        const addr = item.salesOrder.nsShippingAddress?.addr1 ?? 'Unassigned';
        seen.set(`addr:${addr}`, addr);
      }
    }
    return Array.from(seen.entries())
      .map(([val, txt]) => ({ text: txt, value: val }))
      .sort((chkA, chkB) => chkA.text.localeCompare(chkB.text));
  }, [reportData]);

  const serviceFilters = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of reportData ?? []) {
      for (const svc of item.salesOrder.services ?? []) {
        seen.set(svc.id, svc.name);
      }
    }
    return Array.from(seen.entries())
      .map(([val, txt]) => ({ text: txt, value: val }))
      .sort((chkA, chkB) => chkA.text.localeCompare(chkB.text));
  }, [reportData]);

  const columns: ColumnProps<SalesOrderCommissioningReportItem>[] = [
    {
      title: 'Sales Order',
      key: 'salesOrder',
      render: (_, record) => {
        const tranId = record.salesOrder.nsSalesOrder?.tranId ?? '';
        return tranId ? (
          <Link to={`/dashboard/salesOrder/${encodeURIComponent(tranId)}`}>
            {tranId}
          </Link>
        ) : null;
      },
      sorter: (compA, compB) =>
        (compA.salesOrder.nsSalesOrder?.tranId ?? '').localeCompare(
          compB.salesOrder.nsSalesOrder?.tranId ?? '',
        ),
    },
    {
      title: 'Line',
      key: 'line',
      width: 70,
      render: (_, record) => (
        <span>{record.salesOrder.nsSalesOrderLine?.line ?? ''}</span>
      ),
    },
    {
      title: 'Quantity',
      key: 'quantity',
      width: 90,
      render: (_, record) => (
        <span>
          {isNotNull(record.salesOrder.nsSalesOrderLine?.custcol24) &&
          record.salesOrder.nsSalesOrderLine?.custcol24 > 0
            ? record.salesOrder.nsSalesOrderLine?.custcol24
            : record.salesOrder.nsSalesOrderLine?.quantity}
        </span>
      ),
    },
    {
      title: 'Description',
      key: 'description',
      render: (_, record) => (
        <span>{record.salesOrder.nsSalesOrderLine?.description ?? ''}</span>
      ),
    },
    {
      title: 'Organization',
      key: 'organization',
      filters: organizationFilters,
      onFilter: (value, record) => record.salesOrder.organization?.id === value,
      sorter: (compA, compB) =>
        (compA.salesOrder.organization?.name ?? '').localeCompare(
          compB.salesOrder.organization?.name ?? '',
        ),
      render: (_, record) => (
        <span>{record.salesOrder.organization?.name ?? ''}</span>
      ),
    },
    {
      title: 'Building',
      key: 'building',
      filters: buildingFilters,
      onFilter: (value, record) => {
        const bld = record.salesOrder.building;
        if (bld?.id && bld.id !== emptyId) {
          return bld.id === value;
        }
        const addr = record.salesOrder.nsShippingAddress?.addr1 ?? 'Unassigned';
        return `addr:${addr}` === value;
      },
      sorter: (compA, compB) => {
        const nameA =
          compA.salesOrder.building?.id &&
          compA.salesOrder.building.id !== emptyId
            ? compA.salesOrder.building.name
            : (compA.salesOrder.nsShippingAddress?.addr1 ?? '');
        const nameB =
          compB.salesOrder.building?.id &&
          compB.salesOrder.building.id !== emptyId
            ? compB.salesOrder.building.name
            : (compB.salesOrder.nsShippingAddress?.addr1 ?? '');
        return nameA.localeCompare(nameB);
      },
      render: (_, record) => {
        const building = record.salesOrder.building;
        if (building?.id && building.id !== emptyId) {
          if (
            record.salesOrder.organization?.id === userProfile.organization?.id
          ) {
            return (
              <Link
                to={`/dashboard/buildings/${building.id}`}
                target={building.id}
              >
                {building.name}
              </Link>
            );
          }
          return <span>{building.name}</span>;
        }
        return (
          <span style={{ color: 'red' }}>
            {record.salesOrder.nsShippingAddress?.addr1 ?? 'Unassigned'}
          </span>
        );
      },
    },
    {
      title: 'Services',
      key: 'services',
      filters: serviceFilters,
      filterMode: 'menu',
      onFilter: (value, record) =>
        (record.salesOrder.services ?? []).some((svc) => svc.id === value),
      render: (_, record) => (
        <span>
          {(record.salesOrder.services ?? []).map((svc) => (
            <span key={svc.id}>
              {svc.name}
              <br />
            </span>
          ))}
        </span>
      ),
    },
    {
      title: 'Devices',
      key: 'devices',
      render: (_, record) => (
        <span>
          {(record.salesOrder.devices ?? []).map((dev) => (
            <span key={dev.id}>
              {dev.id !== EMPTY_ID &&
                dev.id !== '' &&
                record.salesOrder.organization.id ===
                  userProfile.organization.id && (
                  <Link to={`/dashboard/devices/${dev.id}`} target={dev.id}>
                    {dev.name}
                  </Link>
                )}
              {(dev.id === EMPTY_ID ||
                dev.id === '' ||
                record.salesOrder.organization.id !==
                  userProfile.organization.id) && <span>{dev.name}</span>}
              <br />
            </span>
          ))}
        </span>
      ),
    },
    {
      title: 'Issues',
      key: 'issues',
      filters: ISSUE_FILTERS,
      onFilter: (value, record) => {
        if (value === 'deviceToolKeyMissing') {
          return (
            !record.status.deviceIsMissing && record.status.deviceToolKeyMissing
          );
        }
        return record.status[value as keyof typeof record.status] === true;
      },
      render: (_, record) => (
        <span>
          {record.status.buildingIsMissing && (
            <Tag color="red">No Building</Tag>
          )}
          {record.status.deviceIsMissing && <Tag color="orange">No Device</Tag>}
          {!record.status.deviceIsMissing &&
            record.status.deviceToolKeyMissing &&
            (() => {
              const lines = (record.salesOrder.services ?? []).flatMap(
                (svc) => {
                  const keys = toolKeysByDisplayName.get(svc.name);
                  return keys ? [`${svc.name}: ${keys.join(', ')}`] : [];
                },
              );
              const tooltipContent =
                lines.length > 0
                  ? lines.map((line) => <div key={line}>{line}</div>)
                  : null;
              const toolTipContentWithTitle = (
                <div>
                  <div>
                    Expecting the following keys based on the assigned services:
                    <br />
                    <br />
                  </div>
                  {tooltipContent}
                </div>
              );
              return (
                <Tooltip title={toolTipContentWithTitle}>
                  <Tag color="gold">Missing Tool Key</Tag>
                </Tooltip>
              );
            })()}
        </span>
      ),
    },
    {
      title: 'Start Date',
      key: 'startDate',
      render: (_, record) => (
        <span>{DateStringToUTCDate(record.salesOrder.startDate)}</span>
      ),
      sorter: (compA, compB) =>
        (compA.salesOrder.startDate ?? '').localeCompare(
          compB.salesOrder.startDate ?? '',
        ),
    },
    {
      title: 'Tickets',
      key: 'tickets',
      render: (_, record) => (
        <span>
          {(record.tickets ?? []).map((tkt) => (
            <span key={tkt.id}>
              <Link to={`/dashboard/ticket/${tkt.id}`}>{tkt.number}</Link>
              <br />
            </span>
          ))}
        </span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <span style={{ display: 'flex', gap: '4px' }}>
          {canEdit && (
            <Tooltip title="Edit">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  const tab = record.status.buildingIsMissing
                    ? 'hierarchy'
                    : record.status.deviceIsMissing
                      ? 'devices'
                      : record.status.deviceToolKeyMissing
                        ? 'devices'
                        : 'status';
                  onAction('EditLine', record, tab);
                }}
              />
            </Tooltip>
          )}
          {canCreateTicket && (record.tickets ?? []).length === 0 && (
            <Tooltip title="Create Ticket">
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() => onAction('CreateTicket', record)}
              />
            </Tooltip>
          )}
        </span>
      ),
    },
  ];

  return (
    <IBTable
      rowKey={(record) => record.salesOrder.id}
      columns={columns}
      dataSource={filteredData}
      loading={loading}
      pagination={{
        pageSize,
        showSizeChanger: true,
        onShowSizeChange: (_, size) => setPageSize(size),
      }}
    />
  );
};

export default CommissioningReportTable;
